import { useRef, useCallback, useEffect } from 'react';
import type { DemoStepAudio } from '../../types/demo';

interface UseDemoAudioOptions {
  onAudioEnd: () => void;
  onAudioStart: () => void;
}

/**
 * Hook to manage HTML5 Audio API lifecycle for demo narration + background music.
 * Uses Web Audio API GainNode for reliable background music volume control.
 */
export function useDemoAudio({ onAudioEnd, onAudioStart }: UseDemoAudioOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const bgGainRef = useRef<GainNode | null>(null);
  const bgCtxRef = useRef<AudioContext | null>(null);

  // Create audio elements once
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.preload = 'auto';

    bgMusicRef.current = new Audio();
    bgMusicRef.current.preload = 'auto';
    bgMusicRef.current.loop = true;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current.src = '';
        bgMusicRef.current = null;
      }
      if (bgCtxRef.current) {
        bgCtxRef.current.close().catch(() => {});
        bgCtxRef.current = null;
      }
    };
  }, []);

  const play = useCallback(
    async (audioConfig: DemoStepAudio) => {
      const audio = audioRef.current;
      if (!audio) return;

      audio.src = audioConfig.src;
      audio.onended = () => onAudioEnd();
      audio.onplay = () => onAudioStart();

      try {
        await audio.play();
      } catch {
        // Audio play may be blocked by browser autoplay policy.
        // Fall back to simulating the duration with a timer.
        onAudioStart();
        setTimeout(() => onAudioEnd(), audioConfig.duration);
      }
    },
    [onAudioEnd, onAudioStart],
  );

  const pause = useCallback(() => {
    audioRef.current?.pause();
    bgMusicRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    audioRef.current?.play().catch(() => {});
    bgMusicRef.current?.play().catch(() => {});
    if (bgCtxRef.current?.state === 'suspended') {
      bgCtxRef.current.resume().catch(() => {});
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.src = '';
    }
    if (bgCtxRef.current) {
      bgCtxRef.current.close().catch(() => {});
      bgCtxRef.current = null;
      bgGainRef.current = null;
    }
  }, []);

  const isPlaying = useCallback(() => {
    return audioRef.current ? !audioRef.current.paused : false;
  }, []);

  /** Start looping background music routed through a GainNode at 2% volume */
  const startBgMusic = useCallback((src: string) => {
    const bg = bgMusicRef.current;
    if (!bg) return;

    bg.src = src;
    bg.currentTime = 0;

    // Route through Web Audio API GainNode for reliable volume control
    try {
      const ctx = new AudioContext();
      bgCtxRef.current = ctx;
      const source = ctx.createMediaElementSource(bg);
      const gain = ctx.createGain();
      gain.gain.value = 0.02;
      bgGainRef.current = gain;
      source.connect(gain);
      gain.connect(ctx.destination);
    } catch {
      // Fallback: just set volume property
      bg.volume = 0.02;
    }

    bg.play().catch(() => {});
  }, []);

  /** Stop background music */
  const stopBgMusic = useCallback(() => {
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.src = '';
    }
    if (bgCtxRef.current) {
      bgCtxRef.current.close().catch(() => {});
      bgCtxRef.current = null;
      bgGainRef.current = null;
    }
  }, []);

  return { play, pause, resume, stop, isPlaying, startBgMusic, stopBgMusic };
}
